import AsyncStorage from '@react-native-async-storage/async-storage';
import { invalidateStudentProfilePictureCache } from './studentProfilePhoto';

const STUDENT_ID_KEY = '@kungfuapp/loggedInStudentId';
const USER_SNAPSHOT_KEY = '@kungfuapp/userSnapshot';

/**
 * Logged-in student as returned by GET/PUT `/api/users/:id` (never includes password_hash).
 * `grade_url` / `profile_picture_url` are public Supabase Storage URLs when set.
 *
 * @typedef {Object} StudentUser
 * @property {number} id
 * @property {string} username
 * @property {string|null} [email]
 * @property {string|null} [student_name]
 * @property {string|null} [student_grade]
 * @property {number|null} [student_grade_rank]
 * @property {string[]|null} [courses]
 * @property {boolean} [super_user]
 * @property {boolean} [visible]
 * @property {string|null} [location]
 * @property {string|null} [profile_picture_url]
 * @property {string|null} [grade_url] gradeImageBucket object URL
 * @property {boolean} [is_US_user] when true, dates display in US format (MM/DD/YYYY)
 * @property {boolean} [demoMode] when true, super-user bypass is disabled (grade-only access)
 * @property {boolean} [isHiddenStudent] when true, student is hidden from stats/picklists except true super user
 */

/** Ensures media URL fields exist as string or null (trimmed). */
export function normalizeStudentUserRecord(user) {
  if (!user || typeof user !== 'object') return user;
  const pic =
    user.profile_picture_url != null && String(user.profile_picture_url).trim() !== ''
      ? String(user.profile_picture_url).trim()
      : null;
  const grade =
    user.grade_url != null && String(user.grade_url).trim() !== ''
      ? String(user.grade_url).trim()
      : null;
  const usRaw = user.is_US_user ?? user.is_us_user;
  const is_US_user = usRaw === true || usRaw === 'true' || usRaw === 1;
  const gsRaw = user.showGettingStarted ?? user.show_getting_started;
  const showGettingStarted = gsRaw === false || gsRaw === 'false' || gsRaw === 0 ? false : true;
  const wikRaw = user.weightInKg ?? user.weight_in_kg;
  const weight_in_kg = wikRaw === false || wikRaw === 'false' || wikRaw === 0 ? false : true;
  const hsRaw = user.isHiddenStudent ?? user.is_hidden_student;
  const isHiddenStudent = hsRaw === true || hsRaw === 'true' || hsRaw === 1;
  const gradeNameRaw = user.student_grade ?? user.studentGrade ?? null;
  const student_grade =
    gradeNameRaw != null && String(gradeNameRaw).trim() !== ''
      ? String(gradeNameRaw).trim()
      : null;
  const rankRaw = user.student_grade_rank ?? user.studentGradeRank;
  const student_grade_rank =
    rankRaw != null && !Number.isNaN(Number(rankRaw)) ? Number(rankRaw) : null;
  const focus_exercises = normalizeFocusExercisesFromUser(user);
  const focus_reporting_position = focusReportingPositionFromUser(user);
  return {
    ...user,
    student_grade,
    student_grade_rank,
    profile_picture_url: pic,
    grade_url: grade,
    is_US_user,
    showGettingStarted,
    show_getting_started: showGettingStarted,
    weight_in_kg,
    weightInKg: weight_in_kg,
    isHiddenStudent,
    is_hidden_student: isHiddenStudent,
    focus_exercises,
    focusExercises: focus_exercises,
    focus_reporting_position,
    focusReportingPosition: focus_reporting_position,
  };
}

function normalizeFocusExercisesFromUser(user) {
  const raw = user?.focusExercises ?? user?.focus_exercises ?? [];
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const exerciseId = parseInt(String(item.exercise_id ?? item.exerciseId ?? ''), 10);
    if (!Number.isFinite(exerciseId) || seen.has(exerciseId)) continue;
    const exerciseName = String(item.exercise_name ?? item.exerciseName ?? '').trim();
    if (!exerciseName) continue;
    seen.add(exerciseId);
    out.push({
      exercise_id: exerciseId,
      exercise_name: exerciseName,
      course_id:
        item.course_id != null && !Number.isNaN(Number(item.course_id))
          ? Number(item.course_id)
          : item.courseId != null && !Number.isNaN(Number(item.courseId))
            ? Number(item.courseId)
            : null,
      course_name: String(item.course_name ?? item.courseName ?? '').trim() || null,
      exercise_type_id:
        item.exercise_type_id != null && !Number.isNaN(Number(item.exercise_type_id))
          ? Number(item.exercise_type_id)
          : item.exerciseTypeId != null && !Number.isNaN(Number(item.exerciseTypeId))
            ? Number(item.exerciseTypeId)
            : null,
      exercise_type_name:
        String(item.exercise_type_name ?? item.exerciseTypeName ?? '').trim() || null,
    });
    if (out.length >= 5) break;
  }
  return out;
}

/** Focus exercises selected on the profile page (max 5). */
export function focusExercisesFromUser(user) {
  return normalizeFocusExercisesFromUser(user);
}

/** Where focus reporting widgets render on the home page: 'top' | 'bottom'. */
export function focusReportingPositionFromUser(user) {
  const v = user?.focusReportingPosition ?? user?.focus_reporting_position;
  return String(v ?? '').toLowerCase() === 'bottom' ? 'bottom' : 'top';
}

/** When true (default), show the Getting Started tile on the home page. */
export function showGettingStartedFlag(user) {
  if (!user) return true;
  const v = user.showGettingStarted ?? user.show_getting_started;
  if (v === false || v === 'false' || v === 0) return false;
  return true;
}

/** True when `data` looks like a students row from the API (not `{ error: ... }`). */
export function isUserRecord(data) {
  if (!data || typeof data !== 'object' || data.error != null) return false;
  const id = Number(data.id);
  return !Number.isNaN(id);
}

/** When true (default), weight displays in kg; when false, displays in lbs. */
export function weightInKgFlag(user) {
  if (!user) return true;
  const v = user.weightInKg ?? user.weight_in_kg;
  if (v === false || v === 'false' || v === 0) return false;
  return true;
}

export function isSuperUser(user) {
  if (!user) return false;
  const v = user.super_user;
  return v === true || v === 'true' || v === 1;
}

/** When true, super-user privileges are suppressed (grade-only access). */
export function isDemoMode(user) {
  if (!user) return false;
  const v = user.demoMode ?? user.demo_mode;
  return v === true || v === 'true' || v === 1;
}

/** Super-user access for features and grade bypasses (false when demo mode is on). */
export function hasSuperUserAccess(user) {
  return isSuperUser(user) && !isDemoMode(user);
}

export async function setLoggedInStudentId(id) {
  if (id == null || Number.isNaN(Number(id))) return;
  await AsyncStorage.setItem(STUDENT_ID_KEY, String(id));
}

export async function getLoggedInStudentId() {
  const raw = await AsyncStorage.getItem(STUDENT_ID_KEY);
  if (raw == null || raw === '') return null;
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? null : n;
}

export async function setUserSnapshot(user) {
  if (!isUserRecord(user)) return;
  await AsyncStorage.setItem(
    USER_SNAPSHOT_KEY,
    JSON.stringify(normalizeStudentUserRecord(user))
  );
}

export async function getUserSnapshot() {
  try {
    const s = await AsyncStorage.getItem(USER_SNAPSHOT_KEY);
    if (!s) return null;
    const u = JSON.parse(s);
    if (!isUserRecord(u)) return null;
    return normalizeStudentUserRecord(u);
  } catch {
    return null;
  }
}

export async function clearLoggedInSession() {
  await AsyncStorage.multiRemove([STUDENT_ID_KEY, USER_SNAPSHOT_KEY]);
  invalidateStudentProfilePictureCache();
}
